import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Shield,
  Plus,
  HeartPulse,
  Car,
  Plane,
  Home,
  Umbrella,
  Users,
  MoreVertical,
  ChevronUp,
  ChevronDown,
  XCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IPortalPolicyStatsCardProps } from '@/@types/insurance';
import { useMyInsurancePage } from '@/hooks/useMyInsurancePage';

const INSURANCE_TYPE_CONFIG: Record<
  string,
  { icon: typeof HeartPulse; color: string; bgColor: string }
> = {
  health: { icon: HeartPulse, color: 'text-green-600', bgColor: 'bg-primary/10' },
  car: { icon: Car, color: 'text-blue-500', bgColor: 'bg-blue-500/10' },
  travel: { icon: Plane, color: 'text-orange-500', bgColor: 'bg-orange-500/10' },
  home: { icon: Home, color: 'text-purple-500', bgColor: 'bg-purple-500/10' },
  life: { icon: Umbrella, color: 'text-teal-500', bgColor: 'bg-teal-500/10' },
};

function getTypeConfig(typeName?: string) {
  if (!typeName) return { icon: Shield, color: 'text-muted-foreground', bgColor: 'bg-muted' };
  const lower = typeName.toLowerCase();
  for (const [key, config] of Object.entries(INSURANCE_TYPE_CONFIG)) {
    if (lower.includes(key)) return config;
  }
  return { icon: Shield, color: 'text-muted-foreground', bgColor: 'bg-muted' };
}

const RELATIONSHIP_COLORS: Record<string, string> = {
  primary: 'text-green-600',
  spouse: 'text-purple-500',
  child: 'text-pink-500',
  parent: 'text-blue-500',
  sibling: 'text-orange-500',
  other: 'text-muted-foreground',
};

const AVATAR_COLORS = [
  'bg-green-600 text-white',
  'bg-purple-400 text-white',
  'bg-pink-400 text-white',
  'bg-blue-400 text-white',
  'bg-orange-400 text-white',
];

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function StatsCard({ label, value, valueClassName }: IPortalPolicyStatsCardProps) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-5">
      <span className={cn('text-2xl font-bold', valueClassName ?? 'text-foreground')}>{value}</span>
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  );
}

function DetailField({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span
        className={cn(
          'text-sm',
          bold ? 'font-semibold text-foreground' : 'font-medium text-foreground'
        )}
      >
        {value}
      </span>
    </div>
  );
}

function PolicyCard({
  policy,
  basePath,
}: {
  policy: ReturnType<typeof useMyInsurancePage>['policies'][number];
  basePath: string;
}) {
  const navigate = useNavigate();
  const [depsExpanded, setDepsExpanded] = useState(false);
  const typeConfig = getTypeConfig(policy.insuranceTypeName);
  const TypeIcon = typeConfig.icon;

  const coveragePeriod =
    policy.issuedAt && policy.expiresAt
      ? `${formatDate(policy.issuedAt)} — ${formatDate(policy.expiresAt)}`
      : policy.issuedAt
        ? `From ${formatDate(policy.issuedAt)}`
        : '—';

  const premiumText = policy.premium ? `€ ${policy.premium.toFixed(2)} / month` : '—';

  const coverageUrl = `${basePath}/coverage/${policy._id}`;

  return (
    <div
      className="cursor-pointer overflow-hidden rounded-xl border border-border bg-card transition-shadow hover:shadow-md"
      onClick={() => navigate(coverageUrl)}
      role="link"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(coverageUrl);
        }
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-6 py-5">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-xl',
              typeConfig.bgColor
            )}
          >
            <TypeIcon className={cn('h-5 w-5', typeConfig.color)} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">
              {policy.insuranceTypeName ?? 'Insurance'}
            </h3>
            <p className="text-sm text-muted-foreground">{policy.providerName ?? '—'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge
            variant={
              policy.status === 'active'
                ? 'default'
                : policy.status === 'expired'
                  ? 'destructive'
                  : 'secondary'
            }
            className="capitalize"
          >
            {policy.status === 'under_review' ? 'Under Review' : policy.status}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={e => e.stopPropagation()}
                aria-label="Policy actions"
              >
                <MoreVertical className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                disabled={policy.status !== 'active'}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Cancel Policy
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Details row */}
      <div className="grid grid-cols-4 gap-6 border-b border-border px-6 py-4">
        <DetailField label="Policy Number" value={policy.policyNumber} />
        <DetailField label="Coverage Period" value={coveragePeriod} />
        <DetailField label="Premium" value={premiumText} bold />
        <DetailField label="Plan" value={policy.planName ?? '—'} />
      </div>

      {/* Dependants section */}
      {policy.dependants.length > 0 && (
        <div className="px-6 py-4" onClick={e => e.stopPropagation()}>
          <button
            type="button"
            className="flex w-full items-center justify-between"
            onClick={() => setDepsExpanded(prev => !prev)}
          >
            <div className="flex items-center gap-2">
              <Users className={cn('h-4 w-4', typeConfig.color)} />
              <span className="text-sm font-semibold text-foreground">
                Dependants ({policy.dependants.length})
              </span>
            </div>
            {depsExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>

          {depsExpanded && (
            <div className="mt-3 overflow-hidden rounded-lg border border-border">
              {policy.dependants.map((dep, idx) => (
                <div
                  key={dep.name + dep.relationship}
                  className={cn(
                    'flex items-center gap-4 px-4 py-3',
                    idx % 2 === 0 ? 'bg-muted/30' : 'bg-card',
                    idx < policy.dependants.length - 1 && 'border-b border-border'
                  )}
                >
                  {/* Avatar */}
                  <div
                    className={cn(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-medium',
                      AVATAR_COLORS[idx % AVATAR_COLORS.length]
                    )}
                  >
                    {getInitials(dep.name)}
                  </div>

                  {/* Name & relationship */}
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="text-sm font-medium text-foreground">{dep.name}</span>
                    <span className="text-xs capitalize text-muted-foreground">
                      {dep.relationship}
                    </span>
                  </div>

                  {/* Date of birth */}
                  {dep.dateOfBirth && (
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-[11px] text-muted-foreground">Date of Birth</span>
                      <span className="text-sm text-foreground">{dep.dateOfBirth}</span>
                    </div>
                  )}

                  {/* Member ID */}
                  {dep.memberId && (
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-[11px] text-muted-foreground">Member ID</span>
                      <span className="text-sm text-foreground">{dep.memberId}</span>
                    </div>
                  )}

                  {/* Relationship badge */}
                  <span
                    className={cn(
                      'shrink-0 text-xs font-medium capitalize',
                      RELATIONSHIP_COLORS[dep.relationship] ?? 'text-muted-foreground'
                    )}
                  >
                    {dep.relationship}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PolicyCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-6 py-5">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-xl" />
          <div className="space-y-1.5">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
        <Skeleton className="h-6 w-16 rounded-full" />
      </div>
      <div className="grid grid-cols-4 gap-6 px-6 py-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="space-y-1.5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-4 w-28" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function MyInsurancePage() {
  const { basePath, policies, isLoading, stats } = useMyInsurancePage();
  const navigate = useNavigate();

  const applyPath =
    basePath === '/company/my-insurance'
      ? '/company/my-insurance/apply/new'
      : '/my-insurance/apply/new';

  if (isLoading) {
    return (
      <div className="flex min-h-full flex-col">
        <div className="flex items-center justify-between border-b border-border bg-card px-8 py-5">
          <div className="space-y-1">
            <Skeleton className="h-7 w-36" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-9 w-28" />
        </div>
        <div className="flex flex-col gap-5 p-8">
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
          <PolicyCardSkeleton />
          <PolicyCardSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col">
      {/* Page header */}
      <div className="flex items-center justify-between border-b border-border bg-card px-8 py-5">
        <div>
          <h1 className="text-xl font-semibold text-foreground">My Policies</h1>
          <p className="text-sm text-muted-foreground">
            View and manage your active insurance policies
          </p>
        </div>
        <Button onClick={() => navigate(applyPath)}>
          <Plus aria-hidden="true" className="mr-2 h-4 w-4" />
          Policy
        </Button>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-5 p-8">
        {/* Stats row */}
        <div className="grid grid-cols-4 gap-4">
          <StatsCard label="Total Policies" value={stats.total} />
          <StatsCard label="Active" value={stats.active} valueClassName="text-green-600" />
          <StatsCard label="Expired" value={stats.expired} valueClassName="text-destructive" />
          <StatsCard label="Covered Persons" value={stats.coveredPersons} />
        </div>

        {/* Policy cards */}
        {policies.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card py-24 text-center">
            <Shield className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="text-lg font-medium text-foreground">No policies found</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              You have no insurance policies linked to your account yet.
            </p>
            <Button className="mt-4" onClick={() => navigate(applyPath)}>
              <Plus aria-hidden="true" className="mr-2 h-4 w-4" />
              Apply for Insurance
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {policies.map(policy => (
              <PolicyCard key={policy._id} policy={policy} basePath={basePath} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
